import { useState, useEffect, useRef } from "react";
import DatePicker from "../../activities/DatePicker";
import NewActivityModal, { ActivityData } from "../../activities/modals/NewActivityModal";
import RichTextEditor from "../../../../atoms/RichTextEditor";
import { ExternalLink, Lock, X } from "lucide-react";
import "../../../../../styles/kanbans/modals/edit-tab.css";
import "../../../../../styles/kanbans/modals/edit-job-modal.css";
import api, { getUserUid } from "../../../../../utils/api";
import { API_ENDPOINTS } from "../../../../../config";

const CURRENCIES = ['$', '€', '£', '¥', '₽', 'Fr', 'kr', 'zł', 'R$', 'C$', 'A$'];

const CURRENCY_SYMBOL_TO_CODE: Record<string, string> = {
  '$': 'USD',
  '€': 'EUR',
  '£': 'GBP',
  '¥': 'JPY',
  '₽': 'RUB',
  'Fr': 'CHF',
  'kr': 'SEK',
  'zł': 'PLN',
  'R$': 'BRL',
  'C$': 'CAD',
  'A$': 'AUD',
};

const CURRENCY_CODE_TO_SYMBOL: Record<string, string> = {
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'JPY': '¥',
  'RUB': '₽',
  'CHF': 'Fr',
  'SEK': 'kr',
  'PLN': 'zł',
  'BRL': 'R$',
  'CAD': 'C$',
  'AUD': 'A$',
};

interface JobData {
  uid: string;
  job_title: string;
  company_name: string;
  deadline_date?: string | null;
  vacancy_url?: string | null;
  salary?: string | null;
  salary_currency?: string | null;
  color?: string | null;
  description_text?: string | null;
}

interface EditTabProps {
  jobData: JobData | null;
  jobId?: string;
  boardId?: string;
  defaultColor: string;
  onJobUpdated?: () => void;
  onColorChange?: (color: string | null) => void;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type FieldName = 'company_name' | 'job_title' | 'deadline_date' | 'vacancy_url' | 'salary' | 'color' | 'description_text' | 'linked_cv';

export default function EditTab({ jobData, jobId, boardId, defaultColor, onJobUpdated, onColorChange }: EditTabProps) {
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [showDeadlineCalendar, setShowDeadlineCalendar] = useState(false);
  const [postUrl, setPostUrl] = useState('');
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [salary, setSalary] = useState('');
  const [currency, setCurrency] = useState('$');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [color, setColor] = useState(defaultColor);
  const [description, setDescription] = useState('');
  const [descriptionHtml, setDescriptionHtml] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ [key in FieldName]?: SaveStatus }>({});
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [newActivityModalOpen, setNewActivityModalOpen] = useState(false);
  const [allowedCategories, setAllowedCategories] = useState<string[] | undefined>(undefined);
  const descriptionEditorFocusedRef = useRef(false);
  const lastSavedDescriptionRef = useRef<string>('');
  
  const [selectedCvUid, setSelectedCvUid] = useState<string | null>(null);
  const [showCvDropdown, setShowCvDropdown] = useState(false);
  const cvDropdownRef = useRef<HTMLDivElement>(null);
  const [cvs, setCvs] = useState<Array<{ uid: string; title: string; job_uid: string | null }>>([]);
  const [cvsLoading, setCvsLoading] = useState(false);

  const PRESET_COLORS = [
    '#D1D5DB',
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
    '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
    '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#64748b'
  ];

  useEffect(() => {
    if (jobData) {
      setCompany(jobData.company_name || '');
      setJobTitle(jobData.job_title || '');
      
      if (jobData.deadline_date) {
        const deadlineDate = new Date(jobData.deadline_date);
        if (!isNaN(deadlineDate.getTime())) {
          setDeadline(deadlineDate);
        }
      } else {
        setDeadline(null);
      }
      
      setPostUrl(jobData.vacancy_url || '');
      setIsEditingUrl(!jobData.vacancy_url);
      
      if (jobData.salary) {
        const salaryValue = jobData.salary.replace(/[^\d]/g, '');
        setSalary(salaryValue);
      } else {
        setSalary('');
      }
      
      if (jobData.salary_currency) {
        const normalizedCurrency = jobData.salary_currency.toUpperCase();
        const currencyCode = normalizedCurrency === 'EURO' ? 'EUR' : normalizedCurrency;
        
        if (CURRENCY_CODE_TO_SYMBOL[currencyCode]) {
          setCurrency(CURRENCY_CODE_TO_SYMBOL[currencyCode]);
        } else {
          if (jobData.salary) {
            if (jobData.salary.includes('€')) setCurrency('€');
            else if (jobData.salary.includes('£')) setCurrency('£');
            else if (jobData.salary.includes('¥')) setCurrency('¥');
            else if (jobData.salary.includes('₽')) setCurrency('₽');
            else if (jobData.salary.includes('Fr')) setCurrency('Fr');
            else if (jobData.salary.includes('kr')) setCurrency('kr');
            else if (jobData.salary.includes('zł')) setCurrency('zł');
            else if (jobData.salary.includes('R$')) setCurrency('R$');
            else if (jobData.salary.includes('C$')) setCurrency('C$');
            else if (jobData.salary.includes('A$')) setCurrency('A$');
            else setCurrency('$');
          } else {
            setCurrency('$');
          }
        }
      } else if (jobData.salary) {
        if (jobData.salary.includes('€')) setCurrency('€');
        else if (jobData.salary.includes('£')) setCurrency('£');
        else if (jobData.salary.includes('¥')) setCurrency('¥');
        else if (jobData.salary.includes('₽')) setCurrency('₽');
        else if (jobData.salary.includes('Fr')) setCurrency('Fr');
        else if (jobData.salary.includes('kr')) setCurrency('kr');
        else if (jobData.salary.includes('zł')) setCurrency('zł');
        else if (jobData.salary.includes('R$')) setCurrency('R$');
        else if (jobData.salary.includes('C$')) setCurrency('C$');
        else if (jobData.salary.includes('A$')) setCurrency('A$');
        else setCurrency('$');
      } else {
        setCurrency('$');
      }
      
      setColor(jobData.color || defaultColor);
      
      const descText = jobData.description_text || '';
      
      const shouldUpdate = !descriptionHtml || 
                           (!descriptionEditorFocusedRef.current && descText !== lastSavedDescriptionRef.current);
      
      if (shouldUpdate) {
        setDescription(descText);
        setDescriptionHtml(descText);
        lastSavedDescriptionRef.current = descText;
      }
    } else {
      setCompany('');
      setJobTitle('');
      setDeadline(null);
      setPostUrl('');
      setIsEditingUrl(false);
      setSalary('');
      setCurrency('$');
      setColor(defaultColor);
      setDescription('');
      setDescriptionHtml('');
      lastSavedDescriptionRef.current = '';
      descriptionEditorFocusedRef.current = false;
    }
  }, [jobData, defaultColor]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchCvs = async () => {
      try {
        setCvsLoading(true);
        const cvsData = await api.get<Array<{ uid: string; title: string; job_uid: string | null }>>(API_ENDPOINTS.cvs.list);
        if (Array.isArray(cvsData)) {
          setCvs(cvsData);
          
          if (jobId) {
            const linkedCv = cvsData.find(cv => cv.job_uid === jobId);
            if (linkedCv) {
              setSelectedCvUid(linkedCv.uid);
            } else {
              setSelectedCvUid(null);
            }
          } else {
            setSelectedCvUid(null);
          }
        }
      } catch (err) {
        console.error('Failed to fetch CVs:', err);
        setCvs([]);
      } finally {
        setCvsLoading(false);
      }
    };
    
    fetchCvs();
  }, [jobId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cvDropdownRef.current && !cvDropdownRef.current.contains(event.target as Node)) {
        setShowCvDropdown(false);
      }
    };
    if (showCvDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCvDropdown]);

  const updateCvJobUid = async (cvUid: string, newJobUid: string | null) => {
    try {
      await api.put(API_ENDPOINTS.cvs.updateInfo(cvUid), {
        job_uid: newJobUid,
      });
      
      setCvs(prevCvs => 
        prevCvs.map(cv => 
          cv.uid === cvUid ? { ...cv, job_uid: newJobUid } : cv
        )
      );
    } catch (error) {
      console.error('Failed to update CV job_uid:', error);
      throw error;
    }
  };

  const handleCvSelect = async (newCvUid: string) => {
    if (!jobId) return;

    setSaveStatus(prev => ({ ...prev, linked_cv: 'saving' }));

    try {
      const currentLinkedCv = cvs.find(cv => cv.job_uid === jobId && cv.uid !== newCvUid);
      
      if (currentLinkedCv) {
        await updateCvJobUid(currentLinkedCv.uid, null);
      }
      
      const newCv = cvs.find(cv => cv.uid === newCvUid);
      if (newCv && newCv.job_uid && newCv.job_uid !== jobId) {
        await updateCvJobUid(newCvUid, null);
      }
      
      await updateCvJobUid(newCvUid, jobId);
      
      setSelectedCvUid(newCvUid);
      setShowCvDropdown(false);
      
      setSaveStatus(prev => ({ ...prev, linked_cv: 'saved' }));
      
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, linked_cv: 'idle' }));
      }, 2000);
    } catch (error) {
      console.error('Failed to link CV to job:', error);
      setSaveStatus(prev => ({ ...prev, linked_cv: 'error' }));
      
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, linked_cv: 'idle' }));
      }, 3000);
    }
  };

  const handleCvRemove = async () => {
    if (!selectedCvUid || !jobId) return;

    setSaveStatus(prev => ({ ...prev, linked_cv: 'saving' }));

    try {
      await updateCvJobUid(selectedCvUid, null);
      setSelectedCvUid(null);
      
      setSaveStatus(prev => ({ ...prev, linked_cv: 'saved' }));
      
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, linked_cv: 'idle' }));
      }, 2000);
    } catch (error) {
      console.error('Failed to unlink CV from job:', error);
      setSaveStatus(prev => ({ ...prev, linked_cv: 'error' }));
      
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, linked_cv: 'idle' }));
      }, 3000);
    }
  };

  const debounceSave = (
    changedField: FieldName, 
    deadlineValueOverride?: string | null, 
    colorValueOverride?: string | null,
    currencyOverride?: string | null,
    salaryOverride?: string | null
  ) => {
    if (!jobId || !jobData) return;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    setSaveStatus(prev => ({ ...prev, [changedField]: 'saving' }));

    debounceTimer.current = setTimeout(async () => {
      try {
        let deadlineValue: string | null = null;
        if (deadlineValueOverride !== undefined) {
          deadlineValue = deadlineValueOverride;
        } else if (deadline) {
          deadlineValue = deadline.toISOString();
        }
        
        let colorValue: string | null = null;
        if (colorValueOverride !== undefined) {
          colorValue = colorValueOverride === defaultColor ? null : colorValueOverride;
        } else {
          colorValue = color === defaultColor ? null : color;
        }
        
        const currentCurrency = currencyOverride !== undefined ? currencyOverride : currency;
        const currentSalary = salaryOverride !== undefined ? salaryOverride : salary;
        
        let currencyCode: string | null = null;
        if (currentSalary && currentCurrency) {
          const mappedCode = CURRENCY_SYMBOL_TO_CODE[currentCurrency];
          if (mappedCode) {
            currencyCode = mappedCode;
          } else {
            currencyCode = 'USD';
          }
        }
        
        const payload: any = {
          job_uid: jobId,
          company_name: company || null,
          job_title: jobTitle || null,
          deadline_date: deadlineValue,
          vacancy_url: postUrl || null,
          salary: currentSalary ? currentSalary : null,
          salary_currency: currencyCode,
          color: colorValue,
          description_text: descriptionHtml || null,
        };

        await api.put(API_ENDPOINTS.jobs.update, payload);
        
        if (changedField === 'description_text') {
          lastSavedDescriptionRef.current = descriptionHtml || '';
        }
        
        setSaveStatus(prev => {
          const newStatus = { ...prev };
          Object.keys(newStatus).forEach(key => {
            if (newStatus[key as FieldName] === 'saving') {
              newStatus[key as FieldName] = 'saved';
            }
          });
          return newStatus;
        });
        
        setTimeout(() => {
          setSaveStatus(prev => {
            const newStatus = { ...prev };
            Object.keys(newStatus).forEach(key => {
              if (newStatus[key as FieldName] === 'saved') {
                delete newStatus[key as FieldName];
              }
            });
            return newStatus;
          });
        }, 2000);

        if (onJobUpdated) {
          onJobUpdated();
        }
      } catch (error) {

        setSaveStatus(prev => {
          const newStatus = { ...prev };
          Object.keys(newStatus).forEach(key => {
            if (newStatus[key as FieldName] === 'saving') {
              newStatus[key as FieldName] = 'error';
            }
          });
          return newStatus;
        });
        
        setTimeout(() => {
          setSaveStatus(prev => {
            const newStatus = { ...prev };
            Object.keys(newStatus).forEach(key => {
              if (newStatus[key as FieldName] === 'error') {
                delete newStatus[key as FieldName];
              }
            });
            return newStatus;
          });
        }, 3000);
      }
    }, 1500);
  };

  const handleCompanyChange = (value: string) => {
    setCompany(value);
  };

  const handleCompanyBlur = () => {
    debounceSave('company_name');
  };

  const handleJobTitleChange = (value: string) => {
    setJobTitle(value);
  };

  const handleJobTitleBlur = () => {
    debounceSave('job_title');
  };

  const handleDeadlineChange = (date: Date | null) => {
    setDeadline(date);
    const deadlineValue = date ? date.toISOString() : null;
    debounceSave('deadline_date', deadlineValue);
  };

  const handlePostUrlChange = (value: string) => {
    setPostUrl(value);
  };

  const handlePostUrlBlur = () => {
    debounceSave('vacancy_url');
  };

  const handleSalaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const digitsOnly = value.replace(/\D/g, '');
    
    if (digitsOnly === '' || /^\d+$/.test(digitsOnly)) {
      setSalary(digitsOnly);
    }
  };

  const handleSalaryBlur = () => {
    debounceSave('salary');
  };

  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
    if (salary) {
      debounceSave('salary', undefined, undefined, newCurrency, salary);
    }
  };

  const handleColorChange = (newColor: string) => {
    setColor(newColor);
    const colorValue = newColor === defaultColor ? null : newColor;
    onColorChange?.(colorValue);
    debounceSave('color', undefined, colorValue);
  };

  const handleDescriptionChange = (html: string) => {
    setDescriptionHtml(html);
    setDescription(html);
  };

  const handleDescriptionFocus = () => {
    descriptionEditorFocusedRef.current = true;
  };

  const handleDescriptionBlur = () => {
    descriptionEditorFocusedRef.current = false;
    debounceSave('description_text');
  };

  const handleLogApplication = () => {
    setAllowedCategories(['Apply']);
    setNewActivityModalOpen(true);
  };

  const handleLogInterview = () => {
    setAllowedCategories(['Phone Screen', 'Phone Interview', 'On Site Interview']);
    setNewActivityModalOpen(true);
  };

  const handleLogOffer = () => {
    setAllowedCategories(['Offer Received']);
    setNewActivityModalOpen(true);
  };

  const handleLogOfferAcceptance = () => {
    setAllowedCategories(['Accept Offer', 'Decline Offer']);
    setNewActivityModalOpen(true);
  };

  const handleCreateActivity = async (data: ActivityData) => {
    try {
      const user_uid = getUserUid();
      
      if (!user_uid) {

        return;
      }

      if (!data.board) {

        return;
      }

      if (!boardId) {

        return;
      }

      if (!data.title?.trim()) {

        return;
      }

      const activityPayload = {
        title: data.title.trim(),
        type: data.category as any,
        description: data.note?.trim() || null,
        is_completed: data.completed || false,
        start_date: data.startDate ? new Date(data.startDate).toISOString() : null,
        end_date: data.endDate ? new Date(data.endDate).toISOString() : null,
        board_uid: data.board || boardId,
        job_uid: data.job || jobId || null,
        contact_uid: null,
        status: "active" as const,
      };

      await api.post(API_ENDPOINTS.activities.create, activityPayload);
      
      setNewActivityModalOpen(false);
      setAllowedCategories(undefined);
    } catch (error: any) {

      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else {

      }
    }
  };

  const formatSalary = (value: string): string => {
    if (!value) return '';
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formatDeadline = (date: Date | null): string => {
    if (!date) return '+ set date';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit', 
      year: 'numeric'
    });
  };

  const renderSaveStatus = (field: FieldName) => {
    const status = saveStatus[field];
    if (!status || status === 'idle') return null;

    return (
      <span 
        style={{ 
          fontSize: '11px',
          marginLeft: '6px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontWeight: 500,
        }}
      >
        {status === 'saving' && (
          <span style={{ color: '#6b7280' }}>
            <span style={{ 
              display: 'inline-block',
              width: '10px',
              height: '10px',
              border: '2px solid #6b7280',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.6s linear infinite',
            }} /> Saving...
          </span>
        )}
        {status === 'saved' && (
          <span style={{ color: '#10b981' }}>
            ✓ Saved
          </span>
        )}
        {status === 'error' && (
          <span style={{ color: '#ef4444' }}>
            ✗ Error
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="edit-tab">
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="edit-row">
        <div className="edit-field">
          <label className="edit-label" style={{ display: 'flex', alignItems: 'center' }}>
            Company
            {renderSaveStatus('company_name')}
          </label>
          <input
            type="text"
            className="edit-input"
            value={company}
            onChange={(e) => handleCompanyChange(e.target.value)}
            onBlur={handleCompanyBlur}
          />
        </div>
        <div className="edit-field">
          <label className="edit-label" style={{ display: 'flex', alignItems: 'center' }}>
            Job title
            {renderSaveStatus('job_title')}
          </label>
          <input
            type="text"
            className="edit-input"
            value={jobTitle}
            onChange={(e) => handleJobTitleChange(e.target.value)}
            onBlur={handleJobTitleBlur}
          />
        </div>
        <div className="edit-field">
          <label className="edit-label" style={{ display: 'flex', alignItems: 'center' }}>
            Deadline
            {renderSaveStatus('deadline_date')}
          </label>
          <div className="edit-deadline-wrapper">
            <button
              type="button"
              className="edit-deadline-btn"
              style={{ color: deadline ? '#1f2937' : '#9ca3af' }}
              onClick={() => setShowDeadlineCalendar(!showDeadlineCalendar)}
            >
              {formatDeadline(deadline)}
            </button>
            {showDeadlineCalendar && (
              <DatePicker
                selectedDate={deadline}
                onSelect={(date) => {
                  handleDeadlineChange(date);
                  setShowDeadlineCalendar(false);
                }}
                onClose={() => setShowDeadlineCalendar(false)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="edit-row">
        <div className="edit-field edit-field-double">
          <label className="edit-label" style={{ display: 'flex', alignItems: 'center' }}>
            Post URL
            {renderSaveStatus('vacancy_url')}
          </label>
          <div className="edit-url-wrapper">
            {postUrl && !isEditingUrl ? (
              <>
                <a
                  href={postUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="edit-url-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {postUrl}
                </a>
                <button
                  type="button"
                  className="edit-url-edit-btn"
                  onClick={() => setIsEditingUrl(true)}
                  title="Edit URL"
                >
                  ✎
                </button>
              </>
            ) : (
              <input
                type="text"
                className="edit-input edit-input-placeholder"
                placeholder="+ add URL"
                value={postUrl}
                onChange={(e) => handlePostUrlChange(e.target.value)}
                onBlur={(e) => {
                  handlePostUrlBlur();
                  if (postUrl) setIsEditingUrl(false);
                }}
                autoFocus={isEditingUrl}
              />
            )}
          </div>
        </div>
        <div className="edit-field edit-field-full">
          <label className="edit-label">Application</label>
          <button className="edit-log-btn" onClick={handleLogApplication} type="button">
            + Log Application
          </button>
        </div>
      </div>

      <div className="edit-row">
        <div className="edit-field">
          <label className="edit-label" style={{ display: 'flex', alignItems: 'center' }}>
            Salary
            {renderSaveStatus('salary')}
          </label>
          <div className="edit-salary-wrapper">
            <button
              type="button"
              className="edit-currency-btn"
              onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
            >
              {currency}
            </button>
            {showCurrencyDropdown && (
              <div className="edit-currency-dropdown">
                {CURRENCIES.map((curr) => (
                  <button
                    key={curr}
                    type="button"
                    className={`edit-currency-option ${currency === curr ? 'active' : ''}`}
                    onClick={() => {
                      handleCurrencyChange(curr);
                      setShowCurrencyDropdown(false);
                    }}
                  >
                    {curr}
                  </button>
                ))}
              </div>
            )}
            <input
              type="text"
              className="edit-input edit-input-placeholder edit-salary-input"
              placeholder="+ add salary"
              value={formatSalary(salary)}
              onChange={handleSalaryChange}
              onBlur={handleSalaryBlur}
            />
          </div>
        </div>
        <div className="edit-field">
          <label className="edit-label" style={{ display: 'flex', alignItems: 'center' }}>
            Color
            {renderSaveStatus('color')}
          </label>
          <div className="edit-color-picker-wrapper">
            <button
              type="button"
              className="edit-color-btn"
              style={{ backgroundColor: color }}
              onClick={() => setShowColorPicker(!showColorPicker)}
            />
            {showColorPicker && (
              <div className="edit-color-picker-dropdown">
                <div className="edit-color-grid">
                  {PRESET_COLORS.map((presetColor) => (
                    <button
                      key={presetColor}
                      type="button"
                      className={`edit-color-option ${color === presetColor ? 'active' : ''}`}
                      style={{ backgroundColor: presetColor }}
                      onClick={() => {
                        handleColorChange(presetColor);
                        setShowColorPicker(false);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="edit-field edit-field-full">
          <label className="edit-label">Interviews</label>
          <button className="edit-log-btn" onClick={handleLogInterview} type="button">
            + Log Interview
          </button>
        </div>
      </div>

      <div className="edit-row">
        <div className="edit-field edit-field-double">
          <label className="edit-label" style={{ display: 'flex', alignItems: 'center' }}>
            Linked CV
            {renderSaveStatus('linked_cv')}
          </label>
          <div className="edit-cv-dropdown-wrapper" ref={cvDropdownRef}>
            <div
              className="edit-cv-dropdown-btn"
              onClick={() => setShowCvDropdown(!showCvDropdown)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowCvDropdown(!showCvDropdown);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <span className={selectedCvUid ? '' : 'edit-cv-dropdown-placeholder'}>
                {selectedCvUid
                  ? (() => {
                      const selectedCv = cvs.find(cv => cv.uid === selectedCvUid);
                      return selectedCv ? selectedCv.title : 'Select a CV';
                    })()
                  : 'Select a CV (optional)'}
              </span>
              <div className="edit-cv-dropdown-actions">
                {selectedCvUid && (
                  <>
                    <button
                      type="button"
                      className="edit-cv-dropdown-open"
                      onClick={(e) => {
                        e.stopPropagation();
                        const cvUrl = `/resume-builder/editor/${selectedCvUid}`;
                        window.open(cvUrl, '_blank');
                      }}
                      title="Open CV in new tab"
                    >
                      <ExternalLink size={14} />
                    </button>
                    <button
                      type="button"
                      className="edit-cv-dropdown-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCvRemove();
                      }}
                      title="Remove linked CV"
                    >
                      <X size={14} />
                    </button>
                  </>
                )}
                <span className="edit-cv-dropdown-arrow">▾</span>
              </div>
            </div>
            {showCvDropdown && (
              <div className="edit-cv-dropdown-menu">
                {cvsLoading ? (
                  <div className="edit-cv-dropdown-item edit-cv-dropdown-item--disabled">
                    Loading CVs...
                  </div>
                ) : (
                  <>
                    {cvs.map((cv) => {
                      const isLinkedToOtherJob = cv.job_uid !== null && cv.job_uid !== jobId;
                      const isSelected = selectedCvUid === cv.uid;
                      const isDisabled = isLinkedToOtherJob && !isSelected;
                      
                      return (
                        <div
                          key={cv.uid}
                          className={`edit-cv-dropdown-item ${isSelected ? 'edit-cv-dropdown-item--selected' : ''} ${isDisabled ? 'edit-cv-dropdown-item--disabled' : ''}`}
                          onClick={() => {
                            if (!isDisabled) {
                              handleCvSelect(cv.uid);
                            }
                          }}
                          style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                        >
                          <span>{cv.title}</span>
                          {isDisabled && (
                            <Lock size={14} style={{ marginLeft: 'auto', color: '#9ca3af' }} />
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="edit-row">
        <div className="edit-field edit-field-description">
          <label className="edit-label" style={{ display: 'flex', alignItems: 'center' }}>
            Description
            {renderSaveStatus('description_text')}
          </label>
          <RichTextEditor
            value={descriptionHtml}
            onChange={handleDescriptionChange}
            onFocus={handleDescriptionFocus}
            onBlur={handleDescriptionBlur}
            placeholder="Describe the role, responsibilities, requirements…"
            height={520}
          />
        </div>
        <div className="edit-field-right">
          <div className="edit-field edit-field-full">
            <label className="edit-label">Offer</label>
            <button className="edit-log-btn" onClick={handleLogOffer} type="button">
              + Log Offer
            </button>
          </div>
          <div className="edit-field edit-field-full">
            <label className="edit-label">Offer Acceptance</label>
            <button className="edit-log-btn" onClick={handleLogOfferAcceptance} type="button">
              + Log Offer Acceptance
            </button>
          </div>
        </div>
      </div>

      <NewActivityModal
        open={newActivityModalOpen}
        onClose={() => {
          setNewActivityModalOpen(false);
          setAllowedCategories(undefined);
        }}
        onCommit={handleCreateActivity}
        currentBoard={boardId || undefined}
        currentJob={jobId || undefined}
        allowedCategories={allowedCategories}
      />
    </div>
  );
}
